import { Link } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { FaDatabase } from "react-icons/fa";
import { HiOutlineDocumentReport } from "react-icons/hi";
import { MdMenuOpen } from "react-icons/md";
import { RiMailSettingsLine } from "react-icons/ri";
import { TbLayoutSidebarLeftCollapse, TbLayoutSidebarRightCollapse } from "react-icons/tb";
import {
  Menu,
  MenuItem,
  type MenuItemStyles,
  menuClasses,
  Sidebar,
  SubMenu,
} from "react-pro-sidebar";

import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import type { FileRoutesByTo } from "@/routeTree.gen";

interface MenuLink {
  to: keyof FileRoutesByTo;
  label: string;
}

const AdminDashboardMenuWrapper = ({ children }: { children: ReactNode }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [toggled, setToggled] = useState(false);
  const [broken, setBroken] = useState(false);
  const [openStates, setOpenStates] = useState<Record<string, boolean>>({});

  const iconSize = "20px";

  const reportsLinks: MenuLink[] = [
    { to: "/admindashboard", label: "Summary" },
    { to: "/admindashboard/kpi", label: "KPIs" },
    { to: "/admindashboard/tutor-feedback", label: "Tutor Feedback" },
  ];

  const dataLinks: MenuLink[] = [
    { to: "/admindashboard/events", label: "Events" },
    { to: "/admindashboard/users", label: "Users" },
    { to: "/admindashboard/families", label: "Families" },
    { to: "/admindashboard/staff", label: "Staff" },
    { to: "/admindashboard/classes", label: "Classes" },
    { to: "/admindashboard/transactions", label: "Transactions" },
    { to: "/admindashboard/files", label: "Files" },
    { to: "/admindashboard/tutor-applications", label: "Tutor Apps" },
    { to: "/admindashboard/inquiries", label: "Inquiries" },
    { to: "/admindashboard/subjects", label: "Subjects" },
  ];

  const toolsLinks: MenuLink[] = [
    { to: "/admindashboard/messaging", label: "Messaging" },
    { to: "/admindashboard/configurations", label: "Configurations" },
    { to: "/admindashboard/analytics", label: "Analytics" },
  ];

  const menuItemStyles: MenuItemStyles = {
    root: {
      fontSize: "14px",
      fontWeight: "400",
    },
    icon: {
      color: "#990000", // red-600
      [`&.${menuClasses.disabled}`]: {
        color: "#9ca3af", // gray-400
      },
    },
    SubMenuExpandIcon: {
      color: "#6b7280", // gray-500
    },
    subMenuContent: () => ({
      backgroundColor: "#f8fafc", // slate-50
    }),
    button: {
      [`&.${menuClasses.disabled}`]: {
        color: "#9ca3af", // gray-400
      },
      "&:hover": {
        backgroundColor: "#f1f5f9", // slate-100
        color: "#0f172a", // slate-900
      },
    },
    label: ({ open }) => ({
      fontWeight: open ? 600 : undefined,
    }),
  };

  // When mobile user selects a link, close the menu and collapse the sub menus
  function handleMobileClose() {
    setToggled(false);
    setOpenStates({ reports: false, data: false, tools: false });
  }

  const sideBar = (
    <Sidebar
      id="admin-dashboard-menu"
      collapsed={broken ? false : collapsed}
      toggled={toggled}
      onBackdropClick={() => setToggled(false)}
      onBreakPoint={setBroken}
      breakPoint="md"
      backgroundColor="#ffffff"
      className="border-r border-gray-200"
    >
      <div className="flex flex-col justify-center p-4 mt-8">
        <h2 className={`font-light text-gray-700 ${collapsed ? "text-xs" : "text-base"}`}>
          Admin Dashboard
        </h2>
        <Separator className="mt-2" />
      </div>

      <Menu menuItemStyles={menuItemStyles}>
        {!broken && (
          <MenuItem
            onClick={() => setCollapsed(!collapsed)}
            rootStyles={{
              backgroundColor: "#f8fafc", // slate-50
            }}
            icon={
              collapsed ? (
                <TbLayoutSidebarRightCollapse size={iconSize} />
              ) : (
                <TbLayoutSidebarLeftCollapse size={iconSize} />
              )
            }
          />
        )}

        <SubMenu
          label="Reports"
          icon={<HiOutlineDocumentReport size={iconSize} />}
          onOpenChange={(open) => setOpenStates({ ...openStates, reports: open })}
        >
          {reportsLinks.map((link) => (
            <MenuItem key={link.to} onClick={handleMobileClose} component={<Link to={link.to} />}>
              {link.label}
            </MenuItem>
          ))}
        </SubMenu>

        <SubMenu
          label="Data"
          icon={<FaDatabase size={iconSize} />}
          onOpenChange={(open) => setOpenStates({ ...openStates, data: open })}
        >
          {dataLinks.map((link) => (
            <MenuItem key={link.to} onClick={handleMobileClose} component={<Link to={link.to} />}>
              {link.label}
            </MenuItem>
          ))}
        </SubMenu>

        <SubMenu
          label="Tools"
          icon={<RiMailSettingsLine size={iconSize} />}
          onOpenChange={(open) => setOpenStates({ ...openStates, tools: open })}
        >
          {toolsLinks.map((link) => (
            <MenuItem key={link.to} onClick={handleMobileClose} component={<Link to={link.to} />}>
              {link.label}
            </MenuItem>
          ))}
        </SubMenu>
      </Menu>
    </Sidebar>
  );

  return (
    <div className="min-h-screen flex flex-col w-full" id="auth-menu-container">
      {/* Mobile menu button */}
      {broken && (
        <div className="px-4 py-3 border-b bg-white shadow-sm">
          <Button
            variant="outline"
            className="w-full justify-center border-2 border-red-200 hover:border-red-300 shadow-md hover:shadow-lg transition-all duration-200"
            onClick={() => setToggled(!toggled)}
          >
            <div className="flex items-center gap-2">
              <span className="font-medium">Dashboard Menu</span>
              <MdMenuOpen size={iconSize} />
            </div>
          </Button>
        </div>
      )}

      {/* Main layout */}
      <div className="flex flex-1 w-full">
        {sideBar}

        {/* Main content area - fixed alignment */}
        <div className="flex-1 flex flex-col">
          <main id="dashboard-child-content" className="flex-1 p-6 overflow-auto bg-gray-50">
            <div className="w-full mx-auto">{children}</div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardMenuWrapper;
